'use client'

/**
 * AuthRedirectWrapper
 *
 * Wraps public pages that should redirect authenticated users.
 * Redirect behavior is delegated to useRedirectIfAuthenticated so all auth
 * redirects share the same stale-session safeguards.
 */

import { PageLoader } from '@/components/ui/Loading'
import { useRedirectIfAuthenticated } from '@/hooks/useAuth'

interface AuthRedirectWrapperProps {
  children: React.ReactNode
  redirectTo?: string
  /** Show full-page loader while checking auth state */
  showLoader?: boolean
  /** Custom message for the loader */
  loaderMessage?: string
}

export default function AuthRedirectWrapper({
  children,
  redirectTo = '/invitations',
  showLoader = false,
  loaderMessage,
}: AuthRedirectWrapperProps) {
  const { isLoading } = useRedirectIfAuthenticated(redirectTo)

  // Keep SEO-first behavior by rendering children immediately when showLoader=false.
  if (showLoader && isLoading) {
    return <PageLoader message={loaderMessage || 'Redirecting...'} />
  }

  // Render children for unauthenticated users (or when showLoader is false)
  return <>{children}</>
}
